/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the workspace.

   FormaDock hosts the paper plus every tool panel, exactly the pattern
   the Designer proved (docs/DESIGNER_UNIFIED_PLAN.md §2.8): each panel is
   portalled into whichever container holds it — a dock slot, a floating
   box, or an anchored dropdown — never rewritten per host.

   The panels, the token chip selection + copy/cut/paste triad and every
   zone gesture on the sheet come from useFormaEditorPanels — the SAME set
   the project editor (Designer) mounts. There is NO "Header & Footer"
   panel: the sheet carries the full token model (ZoneBandEdit).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { getPanelData } from 'dockview';
import type { StandardConfig } from '../../shared/types';
import { FormaDock, type FormaDockHandle, type PaletteDef } from '../components/FormaDock';
import { ToolPanel } from '../editor-chrome/ToolPanel';
import { PanelDropdown } from '../editor-chrome/PanelDropdown';
import { isOverDockZone, type FloatingTool } from '../editor-chrome/dock';
import type { FormaSelection } from './useFormaSelection';
import type { PageVariant } from './formaZonesModel';
import type { DesignWiring } from './designWiring';
import { FORMA_PANEL_ICONS, FormaEditorPanels, useFormaEditorPanels } from './useFormaEditorPanels';

export const STUDIO_PALETTES: PaletteDef[] = [
  { id: 'canvas', title: 'Paper', closable: false, noTab: true },
  ...FormaEditorPanels,
];

export const STUDIO_PANEL_ICONS: Record<string, React.ReactNode> = FORMA_PANEL_ICONS;

export interface StudioWorkspaceProps {
  isDark: boolean;
  userId: string;
  locked: boolean;
  forma: StandardConfig | null;
  edit: (fn: (f: StandardConfig) => StandardConfig) => void;
  canEdit: boolean;
  toast: (msg: string, isError?: boolean) => void;

  /** The ONE selection (levels, tokens, EOS, band) — owned by Studio.tsx. */
  sel: FormaSelection;
  pageVariant: PageVariant;
  onPageVariant: (v: PageVariant) => void;
  onDragState: (on: boolean) => void;
  dragActive: boolean;

  floatingTools: FloatingTool[];
  onCloseFloating: (id: string) => void;
  onDockFloating: (id: string) => void;
  dropdown: { id: string; anchor: { left: number; bottom: number } } | null;
  onCloseDropdown: () => void;
  onFloatDropdown: (id: string, at: { left: number; top: number }) => void;
  onUndock: (id: string, at: { left: number; top: number }) => void;
  onDockPanelsChange: (ids: string[]) => void;
  /** Open a tool panel by id — the paper's end-of-section line uses it. */
  onOpenPanel: (id: string) => void;
  onDockReady: (handle: FormaDockHandle) => void;
  panelDragId: string | null;
  firmLayoutKey?: string;

  canvas: (wiring: DesignWiring) => React.ReactNode;
}

export function StudioWorkspace({
  isDark,
  userId,
  locked,
  forma,
  edit,
  canEdit,
  toast,
  sel,
  pageVariant,
  onPageVariant,
  onDragState,
  dragActive,
  floatingTools,
  onCloseFloating,
  onDockFloating,
  dropdown,
  onCloseDropdown,
  onFloatDropdown,
  onUndock,
  onDockPanelsChange,
  onOpenPanel,
  onDockReady,
  panelDragId,
  firmLayoutKey,
  canvas,
}: StudioWorkspaceProps) {
  const [dockEls, setDockEls] = React.useState<Record<string, HTMLElement>>({});
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);

  React.useEffect(() => {
    onDockPanelsChange(Object.keys(dockEls));
  }, [dockEls, onDockPanelsChange]);

  const panels = useFormaEditorPanels({
    forma,
    edit,
    canEdit,
    toast,
    sel,
    pageVariant,
    onPageVariant,
    onDragState,
    dragActive,
    onOpenPanel,
    logTag: 'Studio',
  });

  const titleOf = (id: string) => STUDIO_PALETTES.find((p) => p.id === id)?.title || id;

  const [floatPreview, setFloatPreview] = React.useState<{
    x: number;
    y: number;
    title: string;
  } | null>(null);
  const pendingFloatRef = React.useRef<{ id: string; at: { left: number; top: number } } | null>(
    null
  );
  const onUndockRef = React.useRef(onUndock);
  onUndockRef.current = onUndock;
  const panelDragIdRef = React.useRef(panelDragId);
  panelDragIdRef.current = panelDragId;
  const titleOfRef = React.useRef(titleOf);
  titleOfRef.current = titleOf;

  React.useEffect(() => {
    /** The id being dragged, and whether the dock owns it. */
    const dragging = (): { id: string; docked: boolean } | null => {
      const panelId = getPanelData()?.panelId;
      // The Paper is the anchored core — it never floats (FormaDock already
      // refuses to close it).
      if (panelId && panelId !== 'canvas') return { id: panelId, docked: true };
      const btn = panelDragIdRef.current;
      return btn ? { id: btn, docked: false } : null;
    };
    const onDragOver = (e: DragEvent) => {
      const drag = dragging();
      if (!drag) return;
      if (isOverDockZone(e.clientX, e.clientY)) {
        pendingFloatRef.current = null;
        setFloatPreview(null);
        return;
      }
      // Out here, letting go floats it — say so, and accept the drop so the
      // cursor stops reading "not allowed".
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      // Only a DOCKED panel is completed here; a button drag is finished by
      // the button's own dragend (it knows whether the dock took it).
      pendingFloatRef.current = drag.docked
        ? { id: drag.id, at: { left: e.clientX, top: e.clientY } }
        : null;
      setFloatPreview({ x: e.clientX, y: e.clientY, title: titleOfRef.current(drag.id) });
    };
    const settle = () => {
      const pending = pendingFloatRef.current;
      pendingFloatRef.current = null;
      setFloatPreview(null);
      if (pending) onUndockRef.current(pending.id, pending.at);
    };
    document.addEventListener('dragover', onDragOver, true);
    document.addEventListener('drop', settle, true);
    document.addEventListener('dragend', settle, true);
    return () => {
      document.removeEventListener('dragover', onDragOver, true);
      document.removeEventListener('drop', settle, true);
      document.removeEventListener('dragend', settle, true);
    };
  }, []);

  return (
    <>
      {panels.wrap(
        <div className="dsg-workspace sfs-workspace">
          <FormaDock
            isDark={isDark}
            locked={locked}
            palettes={STUDIO_PALETTES}
            layoutKey={`sf.formaStudioDock.v1.${userId || 'anon'}`}
            firmLayoutKey={firmLayoutKey}
            panelIcons={STUDIO_PANEL_ICONS}
            onContainersChange={setDockEls}
            onReady={(h) => {
              dockHandleRef.current = h;
              onDockReady(h);
            }}
          />

          {dockEls.canvas && createPortal(canvas(panels.wiring), dockEls.canvas)}

          {STUDIO_PALETTES.filter((p) => p.id !== 'canvas').map((p) =>
            dockEls[p.id] ? (
              <React.Fragment key={p.id}>
                {createPortal(panels.renderPanel(p.id), dockEls[p.id])}
              </React.Fragment>
            ) : null
          )}

          {floatingTools
            .filter((f) => !dockEls[f.id])
            .map((f, i) => (
              <ToolPanel
                key={f.id}
                title={titleOf(f.id)}
                index={i}
                at={f.at}
                onDock={() => onDockFloating(f.id)}
                onClose={() => onCloseFloating(f.id)}
              >
                {panels.renderPanel(f.id)}
              </ToolPanel>
            ))}

          {dropdown && !dockEls[dropdown.id] && (
            <PanelDropdown
              title={titleOf(dropdown.id)}
              anchor={dropdown.anchor}
              onFloat={() =>
                onFloatDropdown(dropdown.id, {
                  left: dropdown.anchor.left + 180,
                  top: dropdown.anchor.bottom + 14,
                })
              }
              onClose={onCloseDropdown}
            >
              {panels.renderPanel(dropdown.id)}
            </PanelDropdown>
          )}

          {floatPreview &&
            createPortal(
              <div
                className="dsg-float-preview"
                aria-hidden="true"
                style={{ left: floatPreview.x, top: floatPreview.y }}
              >
                <span className="dsg-float-preview-bar">{floatPreview.title}</span>
                <span className="dsg-float-preview-hint">Release to float</span>
              </div>,
              document.body
            )}
        </div>
      )}
    </>
  );
}
